import { createListenerMiddleware } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import type { Dayjs } from 'dayjs';
import { resolveUserTier, UserTier } from '@/entities/permissions';
import getTimeDifference from '@/shared/lib/date/getTimeDifference';
import type { UserSubscription } from '@/shared/types/auth';
import type { RootState } from '@/store';
import { logout } from '@/store/authActions';
import { setTripTokenFulfilled } from '@/store/slices/tripToken.slice';
import { setUserPermissions } from '@/store/slices/user.slice';

const isDateExpired = (date: Dayjs) => dayjs().isAfter(date);

export const computePermissions = (subscription: UserSubscription | null) => {
  if (!subscription) {
    return {
      isExpired: undefined,
      userTier: resolveUserTier(null, false),
      timeToExpire: undefined
    };
  }
  const { expireAt, status, plan } = subscription;
  const hasSubscriptionType = Boolean(plan?.trim());
  let isExpired: boolean;
  let expiredDate: ReturnType<typeof dayjs> | null = null;
  if (expireAt) {
    const normalized = expireAt.includes('Z') ? expireAt : `${expireAt}Z`;
    expiredDate = dayjs(normalized);
    isExpired = status !== 'active' || isDateExpired(expiredDate);
  } else {
    isExpired = !hasSubscriptionType;
  }
  const timeToExpire = isExpired || !expiredDate ? undefined : getTimeDifference(expiredDate);
  return { isExpired, userTier: resolveUserTier(subscription, isExpired), timeToExpire };
};

export const permissionListener = createListenerMiddleware();

permissionListener.startListening({
  actionCreator: setTripTokenFulfilled,
  effect: async (_action, api) => {
    api.cancelActiveListeners(); // prevent duplicate loops on re-login

    // Set initial values immediately (before first 60s tick)
    const state = api.getState() as RootState;
    api.dispatch(setUserPermissions(computePermissions(state.tripToken.subscription)));

    // Refresh every 60 seconds
    while (true) {
      await api.delay(60_000);
      if (api.signal.aborted) return;
      const current = api.getState() as RootState;
      if (!current.tripToken.subscription) return;
      api.dispatch(setUserPermissions(computePermissions(current.tripToken.subscription)));
    }
  }
});

permissionListener.startListening({
  actionCreator: logout,
  effect: (_action, api) => {
    api.dispatch(
      setUserPermissions({
        isExpired: undefined,
        userTier: UserTier.Public,
        timeToExpire: undefined
      })
    );
  }
});
